// Models
import { CreateSupporterRequestParams, SantizedSupporter } from 'models/Supporter'

// Redux services
import { createOrganizationSupporter, listOrganizationSupporters } from 'redux/services/supporter'

// Local
import { RootState, TypedDispatch } from 'redux/store'

/**
 * Since the query to fetch a Supporter and filter by email can be expensive. We optimize
 * the process by attempting to create a Supporter first before attempting to fetch one.
 *
 * If the creation fails (which will happen if the Supporter already exists) we'll
 * then try and retreive the existing Supporter. For most donors there will not be
 * a pre-existing Supporter.
 *
 * @param store
 * @param dispatch
 * @returns Supporter
 */
const findOrCreateSupporter = async (
  store: RootState,
  oid: string,
  dispatch: TypedDispatch<RootState>,
): Promise<SantizedSupporter> => {
  let supporter: SantizedSupporter | undefined = undefined

  const newSupporter: CreateSupporterRequestParams = {
    first_name: store.order.contact.firstName,
    last_name: store.order.contact.lastName,
    email_address: store.order.contact.email,
    phone: store.order.contact.phone,
    country: store.order.address.value.country,
    state: store.order.address.value.state,
    city: store.order.address.value.city,
    postal_code: store.order.address.value.zipCode,
    address1: store.order.address.value.line1,
    address2: store.order.address.value.line2,
  }

  try {
    const result = await dispatch(
      createOrganizationSupporter.initiate({ orgId: oid, supporter: newSupporter }),
    ).unwrap()

    if (!('error' in result)) {
      supporter = result as SantizedSupporter
    }
  } catch (createSupporterError) {
    try {
      const results = await dispatch(
        listOrganizationSupporters.initiate({
          orgId: oid,
          params: { filter: { email_address: store.order.contact.email } },
        }),
      ).unwrap()

      supporter = results?.[0]
    } catch (findSupporterError) {
      throw new Error('Failed to find or create supporter', {
        cause: { createSupporterError, findSupporterError },
      })
    }
  }

  if (!supporter) {
    throw new Error('Failed to find or create supporter')
  }

  return supporter
}

export default findOrCreateSupporter
